import { useCallback, useEffect, useRef, useState } from 'react';
// `@tiptap/react` re-exports the whole of `@tiptap/core`, so the extension
// primitives come from the same package as the hooks rather than from a second
// dependency that would have to be kept on the same version by hand.
import {
  EditorContent,
  Extension,
  mergeAttributes,
  useEditor,
  useEditorState,
  type Editor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { toast } from 'sonner';
import {
  Bold as BoldIcon,
  Columns2,
  ImagePlus,
  Italic as ItalicIcon,
  Link2,
  Link2Off,
  Palette,
  Trash2,
  Underline as UnderlineIcon,
} from 'lucide-react';
import type { SignatureImportReason, SignatureImportResult } from '@vendorops/shared';
import { client } from '@/lib/api';
import { formatInstant } from '@/lib/date';
import { importPastedImages } from '@/lib/signature-paste';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MailFrame } from '@/components/MailFrame';

/**
 * The signature editor: a deliberately small rich-text surface whose output is
 * email HTML rather than web HTML.
 *
 * ## Why the schema is narrower than a normal editor's
 *
 * `backend/src/lib/signature-sanitize.ts` is the boundary — it re-sanitizes on
 * every save and silently discards anything outside its allowlist. A planner
 * cannot see that happen; they see their formatting evaporate a page-load
 * later. So this editor's schema is kept a strict *subset* of that allowlist:
 * no headings, no code, no strike (`<s>` is not allowed), and every node we do
 * enable renders to a tag the sanitizer keeps.
 *
 * ## Why everything is an inline `style`
 *
 * Mail clients strip `<style>` blocks and ignore stylesheets, so a signature
 * styled by classes arrives unstyled. Nothing here emits a `class` into
 * `getHTML()`; the visible chrome (dashed cell outlines, selection ring) is
 * Tailwind on the *editor's* DOM, which never leaves the browser.
 *
 * ## Why the layout is a table
 *
 * Flexbox does not exist in Outlook. A two-column signature is a two-cell
 * table, which is why "Two columns" is a first-class button rather than a
 * table builder — the planner should never have to know that.
 */

/** Only faces mail clients actually have. Anything else falls back unpredictably. */
const EMAIL_FONTS = [
  { label: 'Helvetica', value: 'Helvetica Neue, Helvetica, Arial, sans-serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times', value: 'Times New Roman, Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Trebuchet', value: 'Trebuchet MS, sans-serif' },
  { label: 'Courier', value: 'Courier New, Courier, monospace' },
];

const FONT_SIZES = ['11px', '12px', '13px', '14px', '15px', '16px', '18px', '20px'];

/** The greys and the link blue a signature actually uses, plus a free picker. */
const SWATCHES = [
  { label: 'Ink', value: '#111111' },
  { label: 'Body grey', value: '#666666' },
  { label: 'Soft grey', value: '#999999' },
  { label: 'Link blue', value: '#1155cc' },
  { label: 'Plum', value: '#7b3f61' },
  { label: 'Sage', value: '#4a6b52' },
];

/**
 * `border-collapse: collapse` so the block reads as layout rather than as a
 * table: mail clients that default to `separate` open a two-pixel gutter
 * between the logo and the text. No `border` declaration — a `<table>` has no
 * border unless one is asked for, and writing `border: none` only produced a
 * degenerate `border: medium` once the browser round-tripped the shorthand.
 */
const LAYOUT_TABLE_STYLE = 'border-collapse: collapse;';

/** One row, two cells, tops aligned, a gutter on the left cell. The reference. */
const TWO_COLUMN_HTML =
  `<table style="${LAYOUT_TABLE_STYLE}"><tbody><tr>` +
  '<td style="vertical-align: top; padding-right: 16px;"><p></p></td>' +
  '<td style="vertical-align: top;"><p></p></td>' +
  '</tr></tbody></table>';

/**
 * Same origin in dev (Vite proxy) and prod (Vercel rewrites); `VITE_API_URL` is
 * the escape hatch for pointing at a backend on another origin. The multipart
 * upload goes out through `fetch` rather than the Hono RPC client because that
 * route has no validator, so the client has no typed shape for a file body.
 */
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

/**
 * A `style` passthrough attribute.
 *
 * TipTap's schema drops every attribute it does not model. Without this, a
 * pasted Gmail signature loses the padding, alignment, and colour that make it
 * a signature, and our own layout table could not carry the cell gutter above.
 * Letting arbitrary declarations through is safe because the sanitizer filters
 * them property by property — an unsupported one is dropped there rather than
 * trusted here.
 */
const styleAttribute = {
  style: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => element.getAttribute('style'),
    renderHTML: (attributes: Record<string, unknown>) =>
      attributes.style ? { style: attributes.style as string } : {},
  },
};

/**
 * TipTap's own `renderHTML` emits a `<colgroup>` of pixel widths and a
 * `min-width` on the table. The sanitizer allows neither, so the stock output
 * would arrive at the server and lose exactly those parts — and column widths
 * would appear to work in the editor and not in the mail. Emitting
 * `<table><tbody>` only means what the planner sees is what survives.
 */
const LayoutTable = Table.extend({
  addAttributes() {
    return { ...this.parent?.(), ...styleAttribute };
  },
  renderHTML({ HTMLAttributes }) {
    // mergeAttributes de-duplicates style declarations by property, so a table
    // that carries its own `border-collapse` (a pasted one) keeps it.
    return ['table', mergeAttributes({ style: LAYOUT_TABLE_STYLE }, HTMLAttributes), ['tbody', 0]];
  },
});

const StyledTableCell = TableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...styleAttribute };
  },
});

const StyledTableHeader = TableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...styleAttribute };
  },
});

/**
 * The paragraph is StarterKit's, so its attributes are widened from the outside
 * rather than by extending it. A Gmail signature carries its colour, size, and
 * line-height on the block itself, and without this every pasted one arrives
 * as unstyled text.
 */
const ParagraphStyle = Extension.create({
  name: 'paragraphStyle',
  addGlobalAttributes() {
    return [{ types: ['paragraph'], attributes: styleAttribute }];
  },
});

/**
 * Link, minus its `class` attribute.
 *
 * TipTap models `class` as a round-tripping attribute on a link, so a signature
 * pasted out of Gmail carries `class="CToWUd"` straight through into the
 * output — the one class this editor would otherwise emit, and a class is
 * precisely what a mail client cannot resolve. `style` takes its place, which
 * is what a link needs anyway: the reference has a blue website and a grey
 * email address, and both are inline colours.
 */
const SignatureLink = Link.extend({
  addAttributes() {
    const attributes: Record<string, unknown> = { ...this.parent?.(), ...styleAttribute };
    delete attributes.class;
    return attributes as ReturnType<NonNullable<typeof this.parent>>;
  },
});

/** Images carry `width` natively; `style` is here for what a paste brings in. */
const SignatureImage = Image.extend({
  addAttributes() {
    return { ...this.parent?.(), ...styleAttribute };
  },
});

/** http/https/mailto — the three schemes the sanitizer will keep on an `href`. */
const LINK_SCHEME = /^(https?|mailto):/i;

/**
 * Turns what a planner types into an href, or null if it cannot be one.
 *
 * A planner types `lillyly.com` or `hello@lillyly.com`, not a URL. Guessing is
 * the whole point of this function; refusing anything the sanitizer would strip
 * is the other half, so a link never disappears between saving and sending.
 */
function normalizeHref(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  if (LINK_SCHEME.test(raw)) return raw;
  // An `@` with no scheme is an address. `mailto:` is the only reading of it
  // that survives the sanitizer, and it is what the planner meant.
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return `mailto:${raw}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return null; // some other scheme; refuse
  return `https://${raw}`;
}

/** True if the pasted markup carries an image we do not already host. */
function hasRemoteImage(html: string): boolean {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return [...doc.querySelectorAll('img')].some((img) => {
    const src = img.getAttribute('src') ?? '';
    return !!src && !src.startsWith('/api/signature/assets/');
  });
}

/** Half the pixel width of an image file, or null if it cannot be measured. */
function halfNaturalWidth(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const probe = new window.Image();
    probe.onload = () => {
      URL.revokeObjectURL(url);
      resolve(probe.naturalWidth ? Math.max(1, Math.round(probe.naturalWidth / 2)) : null);
    };
    probe.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    probe.src = url;
  });
}

// ---------------------------------------------------------------------------
// Importing a signature out of sent mail: what to say about it
// ---------------------------------------------------------------------------

/** `2 images`, `1 image`. Counts read as counts, not as `image(s)`. */
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The one thing to say when we cannot tell what happened.
 *
 * Reached only for a dead connection or a response that is not the shape the
 * route promises — never for a reason the backend named, because every one of
 * those has its own sentence below.
 */
const IMPORT_UNAVAILABLE =
  'That import could not be completed. Try again in a moment, or paste your signature straight in from Gmail.';

/**
 * One sentence per reason the scan came back empty-handed.
 *
 * This is the feature's whole point of contact with the person running it. The
 * import finds her signature by comparing the ends of her sent messages, and
 * everything that comparison depends on — how many emails were readable, how
 * many people she wrote to, whether anything repeated — is invisible to her.
 * The backend goes to the trouble of naming six distinct dead ends so that
 * this function can tell her which one she is in and what to do about it;
 * collapsing them into "import failed" would throw all of that away.
 *
 * `too-few-messages` is the one with a remedy she can act on today — send a
 * few more emails — so its copy says that rather than sending her to pasting,
 * which is where every other dead end ends up.
 *
 * Two rules hold the copy together:
 *
 * - `not-connected` and `no-read-scope` get *different* remedies. Telling a
 *   planner who has never connected Gmail about read access sends her looking
 *   for a permission screen she cannot reach; telling a planner who granted
 *   send-only to connect Gmail sends her to a button she already pressed.
 * - Every not-found case points at pasting, because pasting already works, is
 *   already documented under this editor, and copies images across the same
 *   way. It is the honest fallback rather than a consolation.
 */
function importFailureMessage(reason: SignatureImportReason, scanned: number): string {
  switch (reason) {
    case 'not-connected':
      return 'No Gmail account is connected, so there was no sent mail to read. Connect Gmail at the top of this page, then try this again.';
    case 'no-read-scope':
      return 'Your Gmail connection can send mail but not read it, so nothing was searched. Disconnect Gmail at the top of this page and connect it again, granting read access, then try this again.';
    case 'no-messages':
      return 'There is nothing in your Sent folder yet, so there was nowhere to look. Paste your signature straight in from Gmail instead — it comes across exactly as it looks there.';
    case 'too-few-messages':
      // `scanned: 0` is a different fault from `scanned: 5`, and the backend
      // sends the count precisely so the two do not read as the same sentence:
      // nothing readable is a connection that is failing, and is worth
      // retrying; too thin a sample is not, and asks for something else.
      return scanned === 0
        ? 'Your recent sent emails could not be opened, so there was nothing to compare. Try again in a moment, and paste your signature straight in from Gmail if it keeps happening.'
        : `We found your signature by looking for what repeats at the end of your emails, and ${plural(scanned, 'the one recent email', 'the recent emails')} we could read ${scanned === 1 ? 'is' : 'are'} not enough to tell a signature apart from the rest of a message. Send a few emails to different people and try again, or paste your signature straight in from Gmail.`;
    case 'no-repeated-block':
      return `We compared the ends of your ${plural(scanned, 'recent sent email', 'recent sent emails')} and nothing repeated across them, so there was no signature to pick out. Paste your signature straight in from Gmail instead — it comes across exactly as it looks there.`;
    case 'empty-after-sanitize':
      return 'We found your signature, but nothing in it came through the checks we run before anything is sent to a vendor. Paste it straight in from Gmail instead — that already works, and its images are copied across the same way.';
    default:
      // A reason this build has never heard of. Saying the word back at her
      // would be worse than admitting we do not know.
      return IMPORT_UNAVAILABLE;
  }
}

/**
 * What the planner is looking at, so she can recognise it before she saves it.
 *
 * A signature imported out of the wrong message is indistinguishable from the
 * right one until it is already on every email she sends, and "your email to
 * Amy Chen on September 2" is the detail that makes the difference visible.
 * The image counts are here for the same reason: an image left behind is a gap
 * she should notice now rather than in a vendor's inbox.
 */
function importSuccessMessage(result: Extract<SignatureImportResult, { found: true }>): string {
  const { source, saved, skipped } = result;
  const when = source.sentAt
    ? ` on ${formatInstant(source.sentAt, { month: 'long', day: 'numeric' })}`
    : '';

  const parts = [
    source.recipient
      ? `Imported from your email to ${source.recipient}${when}.`
      : `Imported from a recent email you sent${when}.`,
  ];
  if (saved > 0) {
    parts.push(
      `${plural(saved, 'image was', 'images were')} copied into VendorOps, so they keep working after Gmail's own links expire.`,
    );
  }
  if (skipped > 0) {
    parts.push(
      `${plural(skipped, 'image', 'images')} could not be copied and ${skipped === 1 ? 'was' : 'were'} left out.`,
    );
  }
  parts.push('Check it over before you save.');
  return parts.join(' ');
}

export default function SignatureEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const editorRef = useRef<Editor | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mailImporting, setMailImporting] = useState(false);
  const mailImportingRef = useRef(false);
  /**
   * The outcome of the last import, kept on screen rather than sent through a
   * toast. Both halves need to outlive a four-second timer: the failure copy is
   * an instruction to go and do something at the top of the page, and the
   * success copy is what she checks the imported signature against before
   * saving it.
   */
  const [importNote, setImportNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');

  /** POSTs one Google-hosted URL to the import route; resolves to our own path. */
  const importAsset = useCallback(async (url: string) => {
    const res = await client.api.signature.assets.import.$post({ json: { url } });
    const body = (await res.json().catch(() => null)) as { error?: string; url?: string } | null;
    if (!res.ok || !body?.url) {
      throw new Error(body?.error ?? 'That image could not be brought across.');
    }
    return body.url;
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // A signature is a tight block, and a bare `<p>` is not: mail clients
        // apply the browser default of a 1em margin above and below, which
        // turns five tight lines into a paragraph-spaced list nothing like the
        // signature the planner is copying. `mergeAttributes` merges styles by
        // property, so a pasted paragraph carrying its own margin keeps it.
        paragraph: { HTMLAttributes: { style: 'margin: 0;' } },
        // Everything below either has no tag the sanitizer keeps, or has no
        // business in a signature. Each one left enabled is one more shape the
        // server has to agree with.
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
        strike: false, // renders <s>, which the allowlist does not include
        // Registered explicitly below so the configuration is visible here
        // rather than buried in a kit option.
        link: false,
        underline: false,
      }),
      ParagraphStyle,
      TextStyle,
      Underline,
      SignatureLink.configure({
        // A click inside an editor should place the cursor, not navigate away.
        openOnClick: false,
        defaultProtocol: 'https',
        // Autolink also decides whether the link mark is *inclusive*, and an
        // inclusive one bleeds: type a space and an email address after a
        // linked website and both end up inside the website's anchor, which is
        // exactly the reference signature's last line. Explicit linking, with
        // the button, is the trade — and a planner can see the href they get.
        autolink: false,
        // No `protocols` option: http, https, and mailto are already in
        // linkify's built-in set, and re-registering them only produces
        // "linkifyjs: already initialized" warnings on every mount. The
        // narrowing that matters happens in `isAllowedUri` below.
        // The stock allowlist includes tel:, ftp:, and others the sanitizer
        // will strip. Refusing them here means a link that renders in the
        // editor is a link that survives the save.
        isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && LINK_SCHEME.test(url),
        // `nofollow` is an SEO signal with nothing to say inside an email.
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Color,
      FontFamily,
      FontSize,
      LayoutTable,
      TableRow,
      StyledTableCell,
      StyledTableHeader,
      SignatureImage.configure({
        inline: true,
        // A base64 image would be rejected by the sanitizer at save time, which
        // is the worst moment to discover it. Keeping them out of the document
        // means the only way an image gets in is through storage we control.
        allowBase64: false,
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        id: 'signature-editor',
        role: 'textbox',
        'aria-label': 'Email signature',
        'aria-multiline': 'true',
      },
      /**
       * A signature pasted out of Gmail points at `googleusercontent` URLs that
       * expire, so its logo would vanish weeks later inside vendors' inboxes.
       * We copy the bytes into our own storage before the markup enters the
       * document — which is asynchronous, hence the pending strip below.
       */
      handlePaste: (_view, event) => {
        const html = event.clipboardData?.getData('text/html');
        if (!html || !hasRemoteImage(html)) return false;

        event.preventDefault();
        setImporting(true);

        const failures: string[] = [];
        importPastedImages(html, async (url) => {
          try {
            return await importAsset(url);
          } catch (err) {
            // `importPastedImages` turns a rejection into a dropped image. That
            // is the right behaviour and the wrong silence, so the reason is
            // captured on the way past and surfaced once below.
            failures.push(err instanceof Error ? err.message : 'That image could not be imported.');
            throw err;
          }
        })
          .then((cleaned) => {
            editorRef.current?.chain().focus().insertContent(cleaned).run();
            if (failures.length) {
              toast.error(
                failures.length === 1
                  ? `One image was left out. ${failures[0]}`
                  : `${failures.length} images were left out. ${failures[0]}`,
              );
            }
          })
          .catch(() => toast.error('That signature could not be pasted. Try again.'))
          .finally(() => setImporting(false));

        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      // An empty document still serializes to `<p></p>`. Sending that would
      // store a signature that looks blank and appends a blank line to every
      // email; empty means empty, and the server clears both columns for it.
      onChange(instance.isEmpty ? '' : instance.getHTML());
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  /**
   * Pull in a value the page changed underneath us — the Reset button, or the
   * settings query resolving after the first render. Compared against the
   * editor's own output first: without that, every keystroke would round-trip
   * through the parent and reset the document, taking the cursor with it.
   */
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (value === current) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance.isActive('bold'),
      italic: instance.isActive('italic'),
      underline: instance.isActive('underline'),
      link: instance.isActive('link'),
      inTable: instance.isActive('table'),
      image: instance.isActive('image'),
      imageWidth: String(instance.getAttributes('image').width ?? ''),
      fontFamily: String(instance.getAttributes('textStyle').fontFamily ?? ''),
      fontSize: String(instance.getAttributes('textStyle').fontSize ?? ''),
      color: String(instance.getAttributes('textStyle').color ?? '#111111'),
      href: String(instance.getAttributes('link').href ?? ''),
      html: instance.isEmpty ? '' : instance.getHTML(),
    }),
  });

  /**
   * The preview renders through `MailFrame` — the same inert, un-Tailwinded
   * iframe the composers show before a send. Two implementations of "here is
   * your signature" would eventually disagree about what a signature looks
   * like, and the disagreement would only surface in a vendor's inbox.
   *
   * The empty case differs, which is why the placeholder lives here rather than
   * in the frame: an editor with nothing in it should say so, while a composer
   * belonging to a planner with no signature should show nothing at all.
   */
  const previewHtml = state.html || '<p style="color:#999999">Nothing yet.</p>';

  /**
   * Whether the editing surface is on screen.
   *
   * Starts closed on an empty signature so the first thing a planner sees is
   * the one action that does the work for them — most already have a signature
   * sitting in their sent mail, and a toolbar is a worse offer than a button
   * that fetches it. Building by hand is the fallback, and says so by being
   * the quieter of the two.
   *
   * An existing signature opens the surface immediately: `state.html` is
   * non-empty from the first render in Settings, where the planner came
   * specifically to edit what they already have.
   */
  const [building, setBuilding] = useState(false);
  const [confirmBack, setConfirmBack] = useState(false);
  const revealed = building || state.html !== '';

  const insertTwoColumn = useCallback(() => {
    if (!editor) return;
    const from = editor.state.selection.from;
    editor.chain().focus().insertContent(TWO_COLUMN_HTML).run();

    // Land the cursor in the left cell so the click is followed by typing
    // rather than by hunting for where the block went. The first cell at or
    // after the old selection is the one we just inserted; +2 steps past the
    // cell and its paragraph into the text position inside them.
    let cursor: number | null = null;
    editor.state.doc.nodesBetween(
      Math.max(0, from - 1),
      editor.state.doc.content.size,
      (node, pos) => {
        if (cursor === null && node.type.name === 'tableCell') cursor = pos + 2;
      },
    );
    if (cursor !== null) editor.commands.setTextSelection(cursor);
  }, [editor]);

  const uploadImage = useCallback(
    async (file: File) => {
      if (!editor) return;
      setUploading(true);
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${API_BASE}/api/signature/assets`, {
          method: 'POST',
          credentials: 'include',
          body: form,
        });
        const body = (await res.json().catch(() => null)) as
          | { error?: string; url?: string }
          | null;
        // The route's rejections (too large, not an image, five already) are
        // written for a planner to read, so they are shown verbatim rather than
        // translated into something vaguer.
        if (!res.ok || !body?.url) {
          throw new Error(body?.error ?? 'That image could not be uploaded.');
        }

        // Retina screens are where a logo is inspected and where a 1× one looks
        // soft. Halving the measured width at insert time makes the 2× upload
        // the planner was asked for render at its intended size by default.
        const width = await halfNaturalWidth(file);
        editor
          .chain()
          .focus()
          .setImage({ src: body.url, alt: '', ...(width ? { width } : {}) })
          .run();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'That image could not be uploaded.');
      } finally {
        setUploading(false);
      }
    },
    [editor],
  );

  /**
   * Pull the planner's own signature out of her sent mail.
   *
   * No input, no confirmation, and nothing persisted: the route reads her ten
   * most recent sent messages, copies any images it finds into our storage, and
   * hands back sanitized HTML. It lands in the editor as a draft she reviews and
   * then saves through the page's own Save, which is why this is only offered
   * while the editor is empty — there is no version of this that is allowed to
   * overwrite a signature she has already built.
   */
  const importFromMail = useCallback(async () => {
    // The ref, not the state, is what makes the second click a no-op: two
    // clicks inside one tick both run against the render where `disabled` was
    // still false, and two scans would race each other into the document.
    if (!editor || mailImportingRef.current) return;
    mailImportingRef.current = true;
    setMailImporting(true);
    setImportNote(null);
    try {
      const res = await client.api.signature['import-from-mail'].$post();
      // The not-found body is the same shape at 200 and at 409 — the two
      // precondition reasons only use the error status so a caller that ignores
      // the body still knows something was wrong. So read the body first, and
      // let the reason speak rather than the status code.
      const body = (await res.json().catch(() => null)) as SignatureImportResult | null;

      if (body && body.found === false) {
        setImportNote({ ok: false, text: importFailureMessage(body.reason, body.scanned) });
        return;
      }
      if (!res.ok || body?.found !== true || typeof body.html !== 'string') {
        throw new Error(IMPORT_UNAVAILABLE);
      }

      editor.chain().focus().setContent(body.html).run();
      // `setContent` emits an update of its own, but the parent owns this value
      // and a signature that failed to reach it would be reset to empty by the
      // sync effect on the very next render. Cheap to say twice.
      onChange(editor.isEmpty ? '' : editor.getHTML());
      setImportNote({ ok: true, text: importSuccessMessage(body) });
    } catch {
      // Nothing thrown here is worth showing verbatim: a dead connection gives
      // "Failed to fetch", and a body that is not the shape we expect gives
      // whatever `JSON.stringify` makes of it. Neither tells her anything.
      setImportNote({ ok: false, text: IMPORT_UNAVAILABLE });
    } finally {
      mailImportingRef.current = false;
      setMailImporting(false);
    }
  }, [editor, onChange]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = normalizeHref(linkDraft);
    if (!href) {
      toast.error('That does not look like a web address or an email address.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setLinkOpen(false);
  }, [editor, linkDraft]);

  if (!editor) return null;

  const toolbarButton = (
    key: string,
    label: string,
    active: boolean,
    onClick: () => void,
    icon: React.ReactNode,
  ) => (
    <Button
      key={key}
      type="button"
      size="sm"
      variant={active ? 'secondary' : 'ghost'}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className="size-8 p-0"
      // Keep the selection the button is about to act on. Without this, the
      // mousedown collapses the highlight before the command runs, and the
      // planner watches their selection disappear as they reach for Bold.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {icon}
    </Button>
  );

  /** The chooser, shown in place of the editor while there is nothing to edit. */
  const chooser = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          disabled={mailImporting}
          aria-busy={mailImporting}
          onClick={() => void importFromMail()}
        >
          {mailImporting && <Spinner className="size-4" />}
          Import from your sent emails
        </Button>
        {mailImporting && (
          <p role="status" className="text-xs text-muted-foreground">
            Reading your sent mail…
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          setBuilding(true);
          // The surface is mounting this same tick; focus once it exists.
          // The frame can also land after an unmount — a planner who clicks
          // and immediately navigates away — by which point the editor is
          // torn down and reaching for its commands throws.
          requestAnimationFrame(() => {
            if (!editor.isDestroyed) editor.commands.focus();
          });
        }}
        className="cursor-pointer text-xs font-medium text-primary underline underline-offset-4"
      >
        Build your signature instead
      </button>
    </div>
  );

  if (!revealed) {
    return (
      <div className="space-y-3">
        {chooser}
        {importNote && (
          <p
            data-testid="signature-import-note"
            role="status"
            className={`px-1 text-xs ${importNote.ok ? 'text-muted-foreground' : 'text-destructive'}`}
          >
            {importNote.text}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Belongs to having chosen to build by hand, not to the document being
          empty — a planner who changes their mind three words in needs the
          way out as much as one who changes it immediately.

          It is therefore absent for a signature that was already there when
          the editor opened (Settings, or straight after an import): that
          surface is held open by its own content and there is nothing behind
          it to go back to.

          Leaving discards the draft, because keeping it would mean showing the
          chooser over a signature the parent still holds — and a parent that
          thinks there is something to save while the planner is looking at
          "Import or build?" is the worse confusion. So it asks first, and only
          when there is something to lose: an empty editor goes straight back
          without a dialog nobody needed. */}
      {building && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              // Nothing typed, nothing to lose — leave without asking.
              if (state.html === '') {
                setBuilding(false);
                return;
              }
              setConfirmBack(true);
            }}
            className="cursor-pointer text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            Back
          </button>

          <AlertDialog open={confirmBack} onOpenChange={setConfirmBack}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="display-tight text-2xl font-semibold">
                  Discard this signature?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  What you have written here has not been saved. Going back clears it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep editing</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    editor.commands.clearContent();
                    onChange('');
                    setBuilding(false);
                  }}
                >
                  Discard
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        {/* ── Toolbar ─────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-1 border-b border-border bg-[var(--color-surface-container-high)] px-2 py-2">
          {toolbarButton(
            'bold',
            'Bold',
            state.bold,
            () => editor.chain().focus().toggleBold().run(),
            <BoldIcon className="size-4" />,
          )}
          {toolbarButton(
            'italic',
            'Italic',
            state.italic,
            () => editor.chain().focus().toggleItalic().run(),
            <ItalicIcon className="size-4" />,
          )}
          {toolbarButton(
            'underline',
            'Underline',
            state.underline,
            () => editor.chain().focus().toggleUnderline().run(),
            <UnderlineIcon className="size-4" />,
          )}

          <Separator orientation="vertical" className="mx-1 h-6" />

          <NativeSelect
            size="sm"
            aria-label="Font"
            value={state.fontFamily}
            onChange={(e) => {
              const next = e.target.value;
              if (!next) editor.chain().focus().unsetFontFamily().run();
              else editor.chain().focus().setFontFamily(next).run();
            }}
          >
            <NativeSelectOption value="">Font</NativeSelectOption>
            {EMAIL_FONTS.map((font) => (
              <NativeSelectOption key={font.value} value={font.value}>
                {font.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>

          <NativeSelect
            size="sm"
            aria-label="Font size"
            value={state.fontSize}
            onChange={(e) => {
              const next = e.target.value;
              if (!next) editor.chain().focus().unsetFontSize().run();
              else editor.chain().focus().setFontSize(next).run();
            }}
          >
            <NativeSelectOption value="">Size</NativeSelectOption>
            {FONT_SIZES.map((size) => (
              <NativeSelectOption key={size} value={size}>
                {size.replace('px', '')}
              </NativeSelectOption>
            ))}
          </NativeSelect>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label="Text colour"
                title="Text colour"
                className="size-8 p-0"
              >
                <Palette className="size-4" style={{ color: state.color }} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 space-y-3">
              <div className="grid grid-cols-6 gap-2">
                {SWATCHES.map((swatch) => (
                  <button
                    key={swatch.value}
                    type="button"
                    aria-label={swatch.label}
                    title={swatch.label}
                    className="size-6 rounded-full border border-border"
                    style={{ backgroundColor: swatch.value }}
                    onClick={() => editor.chain().focus().setColor(swatch.value).run()}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Custom colour"
                  className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent"
                  value={/^#[0-9a-f]{6}$/i.test(state.color) ? state.color : '#111111'}
                  onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => editor.chain().focus().unsetColor().run()}
                >
                  Default
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Popover
            open={linkOpen}
            onOpenChange={(open) => {
              setLinkOpen(open);
              if (open) setLinkDraft(state.href);
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={state.link ? 'secondary' : 'ghost'}
                aria-label="Link"
                title="Link"
                className="size-8 p-0"
              >
                <Link2 className="size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-2">
              <Label htmlFor="signature-link">Web or email address</Label>
              <Input
                id="signature-link"
                value={linkDraft}
                placeholder="lillyly.com or hello@lillyly.com"
                onChange={(e) => setLinkDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                }}
              />
              <div className="flex justify-end">
                <Button type="button" size="sm" onClick={applyLink}>
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {state.link &&
            toolbarButton(
              'unlink',
              'Remove link',
              false,
              () => editor.chain().focus().extendMarkRange('link').unsetLink().run(),
              <Link2Off className="size-4" />,
            )}

          <Separator orientation="vertical" className="mx-1 h-6" />

          {toolbarButton(
            'two-column',
            'Insert two-column block',
            false,
            insertTwoColumn,
            <Columns2 className="size-4" />,
          )}

          {state.inTable &&
            toolbarButton(
              'remove-columns',
              'Remove the two-column block',
              false,
              () => editor.chain().focus().deleteTable().run(),
              <Trash2 className="size-4" />,
            )}

          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label="Insert image"
            title="Insert image — upload at 2× the width you want, so it stays sharp"
            className="size-8 p-0"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? <Spinner className="size-4" /> : <ImagePlus className="size-4" />}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Clear first: choosing the same file twice fires no change event
              // otherwise, and a failed upload is exactly when a planner retries
              // with the same file.
              e.target.value = '';
              if (file) void uploadImage(file);
            }}
          />

          {state.image && (
            <div className="ml-1 flex items-center gap-1">
              <Label htmlFor="signature-image-width" className="text-xs text-muted-foreground">
                Width
              </Label>
              <Input
                id="signature-image-width"
                type="number"
                min={16}
                max={1200}
                className="h-8 w-20"
                value={state.imageWidth}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  editor
                    .chain()
                    .focus()
                    .updateAttributes('image', { width: next > 0 ? next : null })
                    .run();
                }}
              />
            </div>
          )}
        </div>

        {/* ── Surface ─────────────────────────────────────────────────────── */}
        {/* All of this styling lives on the editor's DOM, not in the output:
            the dashed cell outlines make the layout block visible while it is
            being built, and are exactly what must not reach a vendor. */}
        <EditorContent
          editor={editor}
          className="[&_.ProseMirror]:min-h-40 [&_.ProseMirror]:p-4 [&_.ProseMirror]:text-sm [&_.ProseMirror]:outline-none [&_.ProseMirror_a]:underline [&_.ProseMirror_img]:inline-block [&_.ProseMirror_table]:my-2 [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-dashed [&_.ProseMirror_td]:border-border [&_.ProseMirror_td]:p-1 [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-dashed [&_.ProseMirror_th]:border-border [&_.ProseMirror-selectednode]:outline [&_.ProseMirror-selectednode]:outline-2 [&_.ProseMirror-selectednode]:outline-primary [&_.tableWrapper]:overflow-x-auto"
        />

        {importing && (
          <div className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <Spinner className="size-3.5" />
            Copying the images out of that signature so they cannot expire…
          </div>
        )}

        {/* A success note outlives the button — after an import the editor is
            no longer empty, and this is the line she checks the result against.
            A failure note does not: every one of them ends in "paste it in
            instead", and leaving that on screen once she has done exactly that
            reads as though the paste failed too. */}
        {importNote && (importNote.ok || state.html === '') && (
          <p
            data-testid="signature-import-note"
            role="status"
            className={`border-t border-border px-4 py-3 text-xs ${
              importNote.ok ? 'text-muted-foreground' : 'text-destructive'
            }`}
          >
            {importNote.text}
          </p>
        )}
      </div>

      {/* ── Preview ─────────────────────────────────────────────────────── */}
      {/* Held back until there is something to show. An empty frame reading
          "Nothing yet" is a box of nothing taking up the room the editor
          wants, and it tells the planner something they can already see. */}
      {state.html !== '' && (
        <div className="space-y-1">
          <p className="label-caps text-xs text-muted-foreground">Preview</p>
          <MailFrame html={previewHtml} title="Signature preview" className="h-32" />
        </div>
      )}
    </div>
  );
}
